import { camelize } from "@ember/string";
import { buildBBCodeAttrs, parseBBCodeTag } from "discourse/lib/text";

let lastSetting;
let lastHosts;

// A bare host, mirroring `AllowedHosts::HOST_FORMAT` on the server, which is
// what the setting validator holds list entries to.
const HOST_FORMAT =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

// Resolves the host the browser would actually connect to, so that percent
// escapes, Unicode and separators are compared after the browser has had its
// say rather than as written. `AllowedHosts.canonical_host` does the same on
// the server.
function canonicalHost(url) {
  let parsed;
  try {
    parsed = new URL(url ?? "");
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") {
    return null;
  }

  return parsed.hostname.replace(/\.$/, "") || null;
}

// Parsed once per setting value because this runs on every location keystroke.
function allowedHosts(setting) {
  if (setting !== lastSetting) {
    lastSetting = setting;
    lastHosts = (setting ?? "")
      .split("|")
      .map((entry) => entry.trim())
      .filter((entry) => HOST_FORMAT.test(entry))
      .map((entry) => canonicalHost(`https://${entry}`))
      .filter(Boolean);
  }

  return lastHosts;
}

// Mirrors DiscourseCalendar::Livestream::AllowedHosts on the server, down to
// canonicalizing the URL rather than pattern-matching it: a location the editor
// presents as a livestream must be one the save path still recognizes, or the
// livestream flag is silently dropped. Subdomains are allowed because livestream
// hosts routinely use them (us06web.zoom.us, www.youtube.com); a host that merely
// ends in an allowed name is not, since the match is anchored to a label boundary.
export function isLivestreamUrl(url, siteSettings) {
  const hosts = allowedHosts(siteSettings?.livestream_allowed_hosts);
  if (!hosts.length) {
    return false;
  }

  const host = canonicalHost(url);
  if (!host) {
    return false;
  }

  return hosts.some(
    (allowed) => host === allowed || host.endsWith(`.${allowed}`)
  );
}

// Mirrors `Event#livestream_url` on the server: location takes precedence,
// url only carries the livestream when no location is set. A blank location
// counts as absent, matching what `buildParams` serializes.
export function livestreamSource(location, url) {
  return location?.trim() ? location : url;
}

// Mirrors `Event#expired?` on the server.
export function hasEventEnded({
  startsAt,
  endsAt,
  recurrence,
  recurrenceUntil,
}) {
  if (recurrence) {
    return !!recurrenceUntil && moment().isAfter(moment(recurrenceUntil));
  }

  const end = endsAt || (startsAt && moment(startsAt).endOf("day"));
  return !!end && moment().isAfter(end);
}

// The recording only becomes relevant once there is something to record, but
// an existing link stays editable even if the dates move forward.
export function showRecordingField({ recording, livestream, ...dates }) {
  return !!recording || (!!livestream && hasEventEnded(dates));
}

export function eventDateInTimezone(value, timezone) {
  if (!value) {
    return null;
  }

  const eventTimezone = timezone || "UTC";
  return moment.isMoment(value)
    ? value.clone().tz(eventTimezone)
    : moment.tz(value, eventTimezone);
}

export function allDayTransition({ startsAt, endsAt, timezone, allDay }) {
  if (allDay) {
    const startDate = (startsAt ?? moment.tz(timezone)).format("YYYY-MM-DD");
    const start = moment.tz(startDate, timezone);
    const sameDay = endsAt?.format("YYYY-MM-DD") === startDate;
    const end =
      !endsAt || sameDay
        ? null
        : moment.tz(endsAt.format("YYYY-MM-DD"), timezone);
    return { startsAt: start, endsAt: end };
  }

  if (!startsAt) {
    return { startsAt, endsAt };
  }

  const now = moment.tz(timezone);
  const start = startsAt
    .clone()
    .hour(now.hour())
    .minute(now.minute())
    .second(0)
    .millisecond(0);
  return { startsAt: start, endsAt: start.clone().add(1, "hour") };
}

export function defaultReminderFor({ startsAt, endsAt, allDay } = {}) {
  const start = startsAt ? moment(startsAt) : null;
  const end = endsAt ? moment(endsAt) : null;
  const isMultiDay = !!(start && end && !start.isSame(end, "day"));

  if (allDay || isMultiDay) {
    return { type: "notification", value: 1, unit: "days", period: "before" };
  }

  return { type: "notification", value: 15, unit: "minutes", period: "before" };
}

export function reminderToBBCode(reminder) {
  const magnitude = Math.abs(parseInt(reminder.value, 10));
  const value = reminder.period === "after" ? -magnitude : magnitude;
  return `${reminder.type}.${value}.${reminder.unit}`;
}

function matchesDefault(reminder, def) {
  return (
    reminder &&
    reminder.value === def.value &&
    reminder.unit === def.unit &&
    reminder.period === def.period
  );
}

// compute the next state when transitioning between attendance modes
export function attendanceTransition({
  mode,
  status,
  maxAttendees,
  reminders,
  previousRsvpStatus,
  previousMaxAttendees,
  value,
}) {
  let nextStatus = status;
  let nextMax = maxAttendees;
  let nextReminders = reminders || [];
  let nextPrevRsvp = previousRsvpStatus;
  let nextPrevMax = previousMaxAttendees;

  if (mode === "none") {
    if (status && status !== "standalone") {
      nextPrevRsvp = status;
    }
    if (maxAttendees) {
      nextPrevMax = maxAttendees;
    }
    nextStatus = "standalone";
    nextMax = null;
    nextReminders = nextReminders.map((r) =>
      r.type === "notification" ? { ...r, type: "bumpTopic" } : r
    );
  } else {
    if (status === "standalone") {
      nextStatus = previousRsvpStatus || "public";
      nextReminders = nextReminders.map((r) =>
        r.type === "bumpTopic" ? { ...r, type: "notification" } : r
      );
    }
    if (mode === "unlimited") {
      if (maxAttendees) {
        nextPrevMax = maxAttendees;
      }
      nextMax = null;
    } else if (mode === "upTo") {
      nextMax = value ?? previousMaxAttendees ?? null;
      if (value != null) {
        nextPrevMax = value;
      }
    }
  }

  return {
    status: nextStatus,
    maxAttendees: nextMax,
    reminders: nextReminders,
    previousRsvpStatus: nextPrevRsvp,
    previousMaxAttendees: nextPrevMax,
  };
}

// if the default is unchanged, swap it out based on the new config
export function reconcileDefaultReminder(reminders, oldConfig, newConfig) {
  if (!reminders || reminders.length !== 1) {
    return reminders;
  }
  const oldDefault = defaultReminderFor(oldConfig);
  const newDefault = defaultReminderFor(newConfig);
  if (matchesDefault(oldDefault, newDefault)) {
    return reminders;
  }
  if (!matchesDefault(reminders[0], oldDefault)) {
    return reminders;
  }
  return [
    {
      ...reminders[0],
      value: newDefault.value,
      unit: newDefault.unit,
      period: newDefault.period,
    },
  ];
}

export function buildParams(startsAt, endsAt, event, siteSettings) {
  const params = {};

  const eventTz = event.timezone || "UTC";

  params.start = event.allDay
    ? moment(startsAt).format("YYYY-MM-DD")
    : eventDateInTimezone(startsAt, eventTz).format("YYYY-MM-DD HH:mm");

  if (event.isClosed) {
    params.closed = "true";
  }

  if (event.status) {
    params.status = event.status;
  }

  if (event.name && event.name.trim()) {
    params.name = event.name;
  }

  if (event.location && event.location.trim()) {
    params.location = event.location;
  }

  if (event.description && event.description.trim()) {
    params.description = event.description;
  }

  if (event.url && event.url.trim()) {
    params.url = event.url;
  }

  if (event.recordingUrl && event.recordingUrl.trim()) {
    params.recording = event.recordingUrl.trim();
  }

  if (event.timezone) {
    params.timezone = event.timezone;
  }

  if (event.recurrence) {
    params.recurrence = event.recurrence;
  }

  if (event.recurrenceUntil) {
    params.recurrenceUntil = moment(event.recurrenceUntil)
      .tz(eventTz)
      .format("YYYY-MM-DD HH:mm");
  }

  if (event.showLocalTime) {
    params.showLocalTime = "true";
  }

  if (event.minimal) {
    params.minimal = "true";
  }

  if (event.chatEnabled) {
    params.chatEnabled = "true";
  }

  if (event.livestream) {
    params.livestream = "true";
  }

  if (event.maxAttendees) {
    params.maxAttendees = `${event.maxAttendees}`;
  }

  if (event.allDay) {
    params.allDay = "true";
  }

  if (endsAt) {
    params.end = event.allDay
      ? moment(endsAt).format("YYYY-MM-DD")
      : eventDateInTimezone(endsAt, eventTz).format("YYYY-MM-DD HH:mm");
  }

  if (event.status === "private") {
    params.allowedGroups = (event.rawInvitees || []).join(",");
  }

  const hosts = (event.hosts || [])
    .map((host) => (typeof host === "string" ? host : host?.username))
    .filter(Boolean);
  if (hosts.length) {
    params.hosts = hosts.join(",");
  }

  if (event.reminders && event.reminders.length) {
    params.reminders = event.reminders.map(reminderToBBCode).join(",");
  }

  if (event.imageUpload?.short_url) {
    params.image = event.imageUpload.short_url;
  } else if (event.imageUpload?.url) {
    params.image = event.imageUpload.url;
  }

  siteSettings.discourse_post_event_allowed_custom_fields
    .split("|")
    .filter(Boolean)
    .forEach((setting) => {
      const param = camelCase(setting);
      if (typeof event.customFields[setting] !== "undefined") {
        params[param] = event.customFields[setting];
      }
    });

  return params;
}

const EVENT_CLOSE_TAG = "[/event]";

export function parseEventBlock(raw) {
  if (!raw) {
    return null;
  }

  let start = raw.indexOf("[event");
  while (start !== -1) {
    const parsed = parseBBCodeTag(raw, start, raw.length);

    if (parsed?.tag === "event" && !parsed.closing) {
      const bodyStart = start + parsed.length;
      const close = raw.indexOf(EVENT_CLOSE_TAG, bodyStart);
      if (close === -1) {
        return null;
      }

      const attrs = {};
      for (const [key, value] of Object.entries(parsed.attrs || {})) {
        if (key !== "_default") {
          attrs[camelize(key)] = value;
        }
      }

      const description = raw
        .slice(bodyStart, close)
        .replace(/^\n/, "")
        .replace(/\n$/, "");

      return {
        full: raw.slice(start, close + EVENT_CLOSE_TAG.length),
        attrs,
        description,
      };
    }

    start = raw.indexOf("[event", start + 1);
  }

  return null;
}

export function buildEventBlock(params, description) {
  const attrs = Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) =>
        key !== "description" && value != null && String(value).trim() !== ""
    )
  );
  const desc = description ? `${description}\n` : "";
  return `[event ${buildBBCodeAttrs(attrs)}]\n${desc}[/event]`;
}

// Changes a single attribute of the event block, leaving everything else in
// the raw as written. A blank value removes the attribute.
export function setEventAttribute(raw, name, value) {
  const parsed = parseEventBlock(raw);
  if (!parsed) {
    return false;
  }

  const attrs = { ...parsed.attrs };
  if (value?.trim()) {
    attrs[name] = value.trim();
  } else {
    delete attrs[name];
  }

  return raw.replace(parsed.full, () =>
    buildEventBlock(attrs, parsed.description)
  );
}

export function getCustomFieldNames(siteSettings) {
  return siteSettings.discourse_post_event_allowed_custom_fields
    .split("|")
    .filter(Boolean);
}

export function customFieldFormName(name) {
  return name.replace(/[.-]/g, "_");
}

// anywhere that builds an event state should use this as the single source of truth for defaults
export function defaultEventState() {
  return {
    name: null,
    location: null,
    description: "",
    timezone: "UTC",
    status: "public",
    maxAttendees: null,
    allDay: false,
    startsAt: null,
    endsAt: null,
    reminders: [],
    recurrence: null,
    recurrenceUntil: null,
    showLocalTime: false,
    chatEnabled: false,
    livestream: false,
    minimal: false,
    url: null,
    recording: null,
    image: null,
    allowedGroups: null,
    closed: false,
    customFields: {},
    hosts: null,
  };
}

export function parseEventAttrs(
  attrs,
  { fallbackTimezone, customFieldNames } = {}
) {
  const tz = attrs.timezone || fallbackTimezone || "UTC";
  const customFields = {};
  (customFieldNames || []).forEach((field) => {
    const param = camelCase(field);
    if (typeof attrs[param] !== "undefined") {
      customFields[field] = attrs[param];
    }
  });

  return {
    ...defaultEventState(),
    name: attrs.name || null,
    location: attrs.location || null,
    timezone: tz,
    status: attrs.status || "public",
    maxAttendees: attrs.maxAttendees ? parseInt(attrs.maxAttendees, 10) : null,
    allDay: attrs.allDay === "true",
    startsAt: attrs.start ? moment.tz(attrs.start, tz) : null,
    endsAt: attrs.end ? moment.tz(attrs.end, tz) : null,
    reminders: parseReminders(attrs.reminders),
    recurrence: attrs.recurrence || null,
    recurrenceUntil: attrs.recurrenceUntil || null,
    showLocalTime: attrs.showLocalTime === "true",
    chatEnabled: attrs.chatEnabled === "true",
    livestream: attrs.livestream === "true",
    minimal: attrs.minimal === "true",
    url: attrs.url || null,
    recording: attrs.recording || null,
    image: attrs.image || null,
    allowedGroups: attrs.allowedGroups || null,
    closed: attrs.closed === "true",
    customFields,
    hosts: attrs.hosts || null,
  };
}

export function stateToEventInput(state) {
  return {
    timezone: state.timezone,
    allDay: state.allDay,
    isClosed: state.closed,
    status: state.status,
    name: state.name,
    location: state.location,
    url: state.url,
    recordingUrl: state.recording,
    recurrence: state.recurrence,
    recurrenceUntil: state.recurrenceUntil,
    showLocalTime: state.showLocalTime,
    minimal: state.minimal,
    chatEnabled: state.chatEnabled,
    livestream: state.livestream,
    maxAttendees: state.maxAttendees,
    rawInvitees: state.allowedGroups ? state.allowedGroups.split(",") : [],
    reminders: state.reminders,
    imageUpload: state.image ? { url: state.image } : null,
    customFields: state.customFields,
    hosts: state.hosts ? state.hosts.split(",") : [],
  };
}

export function parseReminders(reminders) {
  if (!reminders) {
    return [];
  }
  if (Array.isArray(reminders)) {
    return reminders;
  }
  return reminders
    .split(",")
    .map((reminderStr) => {
      const [unit, value, type] = reminderStr.trim().split(".").reverse();
      const numericValue = parseInt(value, 10);
      return {
        type: type || "notification",
        value: Math.abs(numericValue),
        unit,
        period: numericValue < 0 ? "after" : "before",
      };
    })
    .filter((reminder) => reminder.unit && Number.isFinite(reminder.value));
}

export function replaceRaw(params, raw) {
  const parsed = parseEventBlock(raw);
  if (!parsed) {
    return false;
  }
  return raw.replace(parsed.full, () =>
    buildEventBlock(params, params.description)
  );
}

export function camelCase(input) {
  return input
    .toLowerCase()
    .replace(/[-.]/g, "_")
    .replace(/_(.)/g, function (match, group1) {
      return group1.toUpperCase();
    });
}

export function removeEvent(raw) {
  const parsed = parseEventBlock(raw);
  return parsed ? raw.replace(parsed.full, "") : raw;
}

export function buildEventSkeleton(currentUser) {
  const timezone = currentUser?.user_option?.timezone || moment.tz.guess();
  const startsAt = moment.tz(moment(), timezone).startOf("hour").add(1, "hour");
  const endsAt = startsAt.clone().add(1, "hour");
  const reminder = defaultReminderFor({ startsAt, endsAt, allDay: false });
  const defaults = `start="${startsAt.format("YYYY-MM-DD HH:mm")}" end="${endsAt.format("YYYY-MM-DD HH:mm")}" status="public" timezone="${timezone}" reminders="${reminderToBBCode(reminder)}"`;
  return `[event ${defaults}]\n[/event]`;
}
